"use client";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import type { VariantProps } from "class-variance-authority";
import { type ComponentProps, useId, useMemo } from "react";

import { cn } from "../../utils";
import { CheckIcon } from "./CheckIcon";
import { CheckboxRootContext } from "./context";
import { IndeterminateIcon } from "./IndeterminateIcon";
import { labelStyles, rootStyles } from "./style";

type RadixIgnoredProps = "color" | "highContrast" | "variant" | "size" | "orientation";

export interface CheckboxRootProps
  extends Omit<ComponentProps<typeof CheckboxPrimitive.Root>, RadixIgnoredProps>, VariantProps<typeof rootStyles> {
  labelSize?: VariantProps<typeof labelStyles>["size"];
}

export const Root = ({ ref, className, size, labelSize, color = "primary", onChange, ...props }: CheckboxRootProps) => {
  const id = useId();
  const providerValue = useMemo(
    () => ({
      size,
      disabled: props.disabled,
    }),
    [size, props.disabled]
  );

  return (
    <CheckboxRootContext value={providerValue}>
      <div className={cn("flex flex-row items-center gap-2", className)}>
        <CheckboxPrimitive.Root ref={ref} className={cn(rootStyles({ size, color }))} {...props} id={props.id ?? id}>
          <CheckboxPrimitive.Indicator
            forceMount
            className={cn([
              "flex items-center justify-center text-current",
              "transition-all duration-100 ease-in-out",
              "absolute top-1/2 scale-0 opacity-0",
              "data-[state=checked]:-translate-y-1/2 data-[state=checked]:scale-100 data-[state=checked]:opacity-100",
              "data-[state=indeterminate]:-translate-y-1/2 data-[state=indeterminate]:scale-100 data-[state=indeterminate]:opacity-100",
            ])}
          >
            {props.checked === "indeterminate" ? <IndeterminateIcon /> : <CheckIcon />}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>

        {props.children ? (
          <label
            aria-disabled={props.disabled}
            htmlFor={props.id ?? id}
            className={cn(labelStyles({ size: labelSize ?? size }), "group flex flex-col truncate")}
          >
            {props.children}
          </label>
        ) : null}
      </div>
    </CheckboxRootContext>
  );
};

Root.displayName = CheckboxPrimitive.Root.displayName;
